import { WorkerStage } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { type ClipboardScoreLog } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreHistory";
import { type ClipboardScorePenaltyMultipliers } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { type Worker } from "@src/appV2/Worker/api/types";
import { differenceInHours, differenceInMinutes, parseISO } from "date-fns";
import { capitalize, startCase } from "lodash";
import pluralize from "pluralize";

import { type Consequence, type ConsequenceType } from "../components/Activity/ConsequencePill";
import { clipboardScoreActionLabelMap, ClipboardScoreActions } from "./constants";

export function isScoreHealthy(score: number): boolean {
  return score >= 100;
}

export function getTimeLabel(count: number, unit: "minute" | "hour") {
  if (unit === "hour" && count > 24) {
    const days = Math.floor(count / 24);
    return `${days} ${pluralize("day", days)}`;
  }

  if (unit === "minute" && count > 60) {
    const hours = Math.floor(count / 60);
    return getTimeLabel(hours, "hour");
  }

  return `${count} ${pluralize(unit, count)}`;
}

export function getActionLabel(log: ClipboardScoreLog): string {
  const { displayAction, action, shiftStart, clockInTime, createdAt } = log;

  if (
    action === ClipboardScoreActions.START_SHIFT_LATE &&
    isDefined(shiftStart) &&
    isDefined(clockInTime)
  ) {
    const lateTimeInMinutes = differenceInMinutes(parseISO(clockInTime), parseISO(shiftStart), {
      roundingMethod: "floor",
    });
    return `Arrived ${getTimeLabel(lateTimeInMinutes, "minute")} late`;
  }

  if (action === ClipboardScoreActions.CANCEL_SHIFT && isDefined(shiftStart)) {
    const cancellationLeadTimeInHours = differenceInHours(
      parseISO(shiftStart),
      parseISO(createdAt),
      {
        roundingMethod: "ceil",
      }
    );
    return `${getTimeLabel(cancellationLeadTimeInHours, "hour")} notice cancellation`;
  }

  if (action === "CUSTOM_BONUS" && isDefined(displayAction) && displayAction.length > 0) {
    return displayAction;
  }

  const label = clipboardScoreActionLabelMap[action];

  if (isDefined(label)) {
    return label;
  }

  return startCase(capitalize(displayAction ?? action));
}

export function getConsequence(params: {
  action: ClipboardScoreActions;
  score: number;
  adjustment: number;
}): Consequence | undefined {
  const { action, score, adjustment } = params;

  if (action === ClipboardScoreActions.ACCOUNT_RE_ENROLL) {
    return undefined;
  }

  if (action === ClipboardScoreActions.NO_CALL_NO_SHOW) {
    return "Restriction";
  }

  if (adjustment === 0) {
    return adjustment;
  }

  if (score <= 0 && adjustment < 0 && score - adjustment > 0) {
    return adjustment;
  }

  if (score <= 0) {
    return "On Restriction";
  }

  return adjustment;
}

export function getConsequenceType(action: ClipboardScoreActions): ConsequenceType {
  return action === ClipboardScoreActions.WORK_SHIFT ||
    action === ClipboardScoreActions.CLOCKIN_TIMELINESS_BONUS
    ? "bonus"
    : "penalty";
}

export function areMultipliersActive(multipliers?: ClipboardScorePenaltyMultipliers): boolean {
  return (
    isDefined(multipliers) && (multipliers.lateness.next > 1 || multipliers.cancellation.next > 1)
  );
}

export function isWorkerInviteRequired({ stage, substage }: Pick<Worker, "stage" | "substage">) {
  return stage === WorkerStage.RESTRICTED && substage?.name === "INVITE_REQUIRED";
}
